import {
    AbfLoanExpanded,
    BsMetaUtil,
    ClaimableRaydiumFeeResult,
    getLoanStrategyIdentifier,
    useRaydiumLiquidityTransaction
} from "@bridgesplit/abf-react";
import { RaydiumPositionExpanded, TxnManageRaydiumLiquidityParams } from "@bridgesplit/abf-sdk";
import { Image, RAYDIUM_LOGO, Row, Text } from "@bridgesplit/ui";
import { formatAddress, formatUsd, LOADING_ERROR, Result } from "@bridgesplit/utils";
import { EnergySavingsLeafOutlined } from "@mui/icons-material";

import { useTransactionSender } from "../transactions";
import { AppButton } from "./auth";
import { AUTO_SLIPPAGE_DECIMALS } from "../loan/actions/raydium/RaydiumContext";

export function RaydiumPositionInfo({ position }: { position: RaydiumPositionExpanded | undefined }) {
    return (
        <Row spacing={1}>
            <Text>{BsMetaUtil.formatAmount(position?.tokenA?.metadata, position?.tokenA?.amount)} </Text>
            <Text color="disabled">/</Text>
            <Text>{BsMetaUtil.formatAmount(position?.tokenB?.metadata, position?.tokenB?.amount)} </Text>
        </Row>
    );
}

export function RaydiumPositionSummarized({
    position,
    spaceBetween
}: {
    position: RaydiumPositionExpanded | undefined;
    spaceBetween?: boolean;
}) {
    return (
        <Row sx={{ width: "100%" }} spaceBetween={spaceBetween} spacing={1}>
            <Row spacing={1}>
                <Image src={RAYDIUM_LOGO} size="16px" variant="circle" />
                <Text>{formatAddress(position?.position.nftMint)}</Text>
            </Row>
            <Text color="caption">{formatUsd(position?.totalPrice)}</Text>
        </Row>
    );
}

export function parseRaydiumTicks(raydiumPosition: RaydiumPositionExpanded | undefined) {
    if (!raydiumPosition) return undefined;
    try {
        const { position, pool } = raydiumPosition;

        return {
            currentTick: pool.tickCurrent,
            tickLower: position.tickLowerIndex,
            tickUpper: position.tickUpperIndex
        };
    } catch (error) {
        Result.err(error);
        return undefined;
    }
}

// In order to claim fees on raydium, we need to set all token inputs to zero.
const CLAIM_RAYDIUM_AMOUNT = 0;
export function ClaimRaydiumFees({
    raydiumPosition,
    loanExpanded,
    claimableFees,
    isActionTypeInitialized,
    reset
}: {
    raydiumPosition: RaydiumPositionExpanded;
    loanExpanded: AbfLoanExpanded | undefined;
    claimableFees: ClaimableRaydiumFeeResult;
    isActionTypeInitialized: boolean | undefined;
    reset: () => void;
}) {
    const decreaseLiquidity = useRaydiumLiquidityTransaction();
    const send = useTransactionSender();

    async function submit() {
        if (!loanExpanded || !isActionTypeInitialized) return Result.errFromMessage(LOADING_ERROR);
        const strategyIdentifier = getLoanStrategyIdentifier(loanExpanded);
        if (!strategyIdentifier) return Result.errFromMessage("Can't claim fees on a Prime loan");

        const params: TxnManageRaydiumLiquidityParams = {
            loan: loanExpanded.loan.address,
            positionMint: raydiumPosition.position.nftMint,
            increase: false,
            amount: CLAIM_RAYDIUM_AMOUNT,
            manageParams: {
                collateralIndex: 0,
                liquidityAmount: CLAIM_RAYDIUM_AMOUNT,
                manageParams: {
                    tokenAAmount: CLAIM_RAYDIUM_AMOUNT,
                    tokenBAmount: CLAIM_RAYDIUM_AMOUNT
                }
            },
            slippageDecimals: AUTO_SLIPPAGE_DECIMALS
        };
        return await send(decreaseLiquidity, params, {
            sendOptions: { onSuccess: reset },
            preventAutoDialogClose: true
        });
    }

    return (
        <Row spacing={1}>
            <Text>{formatUsd(claimableFees.feesUsd)}</Text>
            <AppButton
                variant="text"
                sx={{ ":hover": { background: "none", opacity: 0.6 } }}
                color="success"
                width="max-content"
                isTransaction
                disabled={!isActionTypeInitialized}
                asyncCta={{
                    onClickWithResult: submit
                }}
            >
                Claim <EnergySavingsLeafOutlined />
            </AppButton>
        </Row>
    );
}
